import { computed, inject, Injectable, Signal } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { Router } from '@angular/router'
import { map, startWith } from 'rxjs'

/**
 * ActiveRoute - Service for tracking the current active route
 *
 * Provides a reactive way to check if a route is active based on the current URL.
 * Useful for navigation components that need to highlight active items.
 *
 * Usage:
 * ```ts
 * activeRoute = inject(ActiveRoute)
 * isHomeActive = computed(() => this.activeRoute.isActive('/'))
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class ActiveRoute {
  #router = inject(Router)

  /**
   * Signal containing the current URL
   */
  currentUrl = toSignal(
    this.#router.events.pipe(
      startWith(null),
      map(() => this.#router.url)
    ),
    { initialValue: this.#router.url }
  )

  /**
   * Check if a given route is currently active
   * @param route - The route path to check (e.g., '/', '/team')
   * @returns true if the route is active, false otherwise
   *
   * Special case: Home route ('/') requires exact match
   * Other routes: Returns true if current URL starts with the given route
   */
  isActive(route: string): boolean {
    const currentUrl = this.currentUrl()
    if (route === '/') return currentUrl === '/'

    return currentUrl?.startsWith(route) ?? false
  }

  /**
   * Creates a computed signal that tracks if a route is active
   * @param route - The route path to track
   * @returns A computed signal that returns true when the route is active
   *
   * Example:
   * ```ts
   * isTeamActive = this.activeRoute.isActiveSignal('/team')
   * ```
   */
  isActiveSignal(route: string): Signal<boolean> {
    return computed(() => this.isActive(route))
  }
}
